import type { ReactNode } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Props {
  id: string;
  isChild?: boolean;
  /** Profondeur hiérarchique (0 = racine, 1 = sous, 2 = sous-sous). */
  depth?: number;
  icon?: string | null;
  image?: string | null;
  name: string;
  typeLabel?: string | null;
  count?: number;
  countLabel?: string;
  actions?: ReactNode;
}

/**
 * Ligne de catégorie réordonnable par glisser-déposer.
 * Utilisée dans la gestion des catégories (produits & services) pour
 * définir l'ordre d'affichage sur la boutique publique.
 */
export default function SortableCategoryItem({
  id,
  isChild = false,
  depth,
  icon,
  image,
  name,
  typeLabel,
  count,
  countLabel = 'produit',
  actions,
}: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const level = depth != null ? depth : (isChild ? 1 : 0);
  const indentClass =
    level >= 2
      ? 'ml-10 border-l-2 border-secondary-200/70'
      : level === 1
        ? 'ml-5 border-l-2 border-accent-200/70'
        : '';

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 30 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between px-2.5 py-2 rounded-lg bg-background-50 border border-transparent hover:border-background-200/70 hover:bg-background-100/70 transition-all duration-200 group ${indentClass}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <button
          type="button"
          {...attributes}
          {...listeners}
          onClick={(e) => e.preventDefault()}
          title="Glisser pour réordonner"
          aria-label={`Réordonner ${name}`}
          className="w-6 h-6 flex items-center justify-center rounded text-foreground-300 hover:text-foreground-700 hover:bg-background-200/70 cursor-grab active:cursor-grabbing touch-none flex-shrink-0"
        >
          <i className="ri-drag-move-2-line text-sm"></i>
        </button>

        {icon ? (
          <div className="w-8 h-8 rounded-md bg-accent-50 flex items-center justify-center flex-shrink-0">
            <i className={`${icon} text-sm text-accent-600`}></i>
          </div>
        ) : image ? (
          <div className="w-8 h-8 rounded-md overflow-hidden bg-background-100 flex-shrink-0">
            <img src={image} alt={name} className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="w-8 h-8 rounded-md bg-background-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-folder-line text-sm text-foreground-400"></i>
          </div>
        )}

        <div className="min-w-0 flex items-center gap-2">
          <span className="text-sm font-medium text-foreground-800 truncate">{name}</span>
          {typeLabel && (
            <span className="px-2 py-0.5 bg-secondary-50 text-secondary-600 rounded-full text-xs whitespace-nowrap">{typeLabel}</span>
          )}
          {typeof count === 'number' && (
            <span className="px-2 py-0.5 bg-primary-50 text-primary-700 rounded-full text-xs font-semibold whitespace-nowrap">
              {count} {countLabel}{count > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {actions && <div className="flex items-center gap-0.5 flex-shrink-0">{actions}</div>}
    </div>
  );
}